<template>
  <div class="article-list-page">
    <header class="page-header">
      <div class="container">
        <div class="header-row">
          <div class="header-left">
            <h2 class="current-category-title" v-if="!searchKeyword">{{ currentCategoryName }}</h2>
            <h2 class="current-category-title" v-else>全站搜索结果 - "{{ searchKeyword }}"</h2>
            <nav class="breadcrumb" v-if="!searchKeyword">
              <a href="/">首页</a>
              <span v-for="(crumb, index) in breadcrumbList" :key="crumb.id">
                > <a href="#" @click.prevent="filterByCategory(crumb.id)">{{ crumb.name }}</a>
              </span>
            </nav>
          </div>          
        </div>
        <div class="search-box">
            <input type="text" v-model="searchKeyword" :placeholder="searchKeyword ? '搜索全站文章' : '搜索本栏目文章'" @keyup.enter="doSearch" />
            <button @click="doSearch">搜索</button>
          </div>
      </div>
    </header>

    <div class="container">
      <aside class="sidebar" v-if="!searchKeyword">
        <div class="sidebar-section">
          <h3>文章分类</h3>
          <ul class="category-list">
            <li v-for="cat in categories" :key="cat.id" class="tree-item">
              <div class="tree-row">
                <span v-if="cat.children && cat.children.length > 0" 
                      class="expand-icon" 
                      @click.prevent="toggleExpand(cat.id)">
                  {{ expandedCats.includes(cat.id) ? '▼' : '▶' }}
                </span>
                <span v-else class="expand-icon empty"></span>
                <a href="#" @click.prevent="filterByCategory(cat.id)" 
                   :class="{ active: activeCategoryId === cat.id }">
                  {{ cat.name }} ({{ cat.count }})
                </a>
              </div>
              <ul v-if="cat.children && cat.children.length > 0 && expandedCats.includes(cat.id)" class="sub-category">
                <li v-for="sub1 in cat.children" :key="sub1.id" class="tree-item">
                  <div class="tree-row">
                    <span v-if="sub1.children && sub1.children.length > 0" 
                          class="expand-icon" 
                          @click.prevent="toggleExpand(sub1.id)">
                      {{ expandedCats.includes(sub1.id) ? '▼' : '▶' }}
                    </span>
                    <span v-else class="expand-icon empty"></span>
                    <a href="#" @click.prevent="filterByCategory(sub1.id)" 
                       :class="{ active: activeCategoryId === sub1.id }">
                      {{ sub1.name }} ({{ sub1.count }})
                    </a>
                  </div>
                  <ul v-if="sub1.children && sub1.children.length > 0 && expandedCats.includes(sub1.id)" class="sub-category">
                    <li v-for="sub2 in sub1.children" :key="sub2.id" class="tree-item">
                      <div class="tree-row">
                        <span class="expand-icon empty"></span>
                        <a href="#" @click.prevent="filterByCategory(sub2.id)" 
                           :class="{ active: activeCategoryId === sub2.id }">
                          {{ sub2.name }} ({{ sub2.count }})
                        </a>
                      </div>
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
        </div>
      </aside>

      <div class="main-content" :class="{ 'full-width': searchKeyword }">
        <div class="article-list">
          <template v-for="article in articles">
            <div v-if="article.external_link" :key="'ext-'+article.id" class="article-item" @click="handleExternalLink(article)">
              <span class="article-bullet">•</span>
              <span class="article-title">{{ article.title }}</span>
              <svg class="external-link-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
              <span class="article-date">{{ article.date }}</span>
            </div>
            <div v-else :key="'int-'+article.id" class="article-item" @click="goToDetail(article)">
              <span class="article-bullet">•</span>
              <span class="article-title">{{ article.title }}</span>
              <span class="article-date">{{ article.date }}</span>
            </div>
          </template>
        </div>

        <div class="pagination-wrapper">
          <span class="total-text">共 {{ totalItems }} 条</span>
          <button class="page-btn" :disabled="currentPage === 1" @click="prevPage">&lt;</button>
          
          <template v-if="totalPages <= 7">
            <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
              {{ page }}
            </button>
          </template>
          
          <template v-else>
            <template v-if="currentPage <= 4">
              <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
              <span class="ellipsis">...</span>
              <button class="page-btn" @click="goToPage(totalPages)">{{ totalPages }}</button>
            </template>
            
            <template v-else-if="currentPage >= totalPages - 3">
              <button class="page-btn" @click="goToPage(1)">1</button>
              <span class="ellipsis">...</span>
              <button v-for="page in visiblePages" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
            </template>
            
            <template v-else>
              <button class="page-btn" @click="goToPage(1)">1</button>
              <span class="ellipsis">...</span>
              <button v-for="page in visiblePages.slice(2, 5)" :key="page" class="page-btn" :class="{ active: currentPage === page }" @click="goToPage(page)">
                {{ page }}
              </button>
              <span class="ellipsis">...</span>
              <button class="page-btn" @click="goToPage(totalPages)">{{ totalPages }}</button>
            </template>
          </template>
          
          <button class="page-btn" :disabled="currentPage >= totalPages" @click="nextPage">&gt;</button>
          <select class="page-size-select" v-model="pageSize" @change="onPageSizeChange">
            <option value="10">10 条/页</option>
            <option value="20">20 条/页</option>
            <option value="30">30 条/页</option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ArticleList',
  data() {
    return {
      articles: [],
      hotArticles: [],
      categories: [],
      flatCategories: [],
      currentPage: 1,
      pageSize: 10,
      totalPages: 1,
      totalItems: 0,
      activeCategoryId: 2,
      expandedCats: [],
      defaultRootCat: { id: 2, name: '新闻中心' },
      searchKeyword: ''
    }
  },
  computed: {
    breadcrumbList() {
      return this.getBreadcrumbPath(this.activeCategoryId)
    },
    currentCategoryName() {
      const cat = this.flatCategories.find(c => c.id === this.activeCategoryId)
      if (cat && cat.name) {
        return cat.name
      }
      return this.defaultRootCat.name
    },
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
        } else if (current >= total - 3) {
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push(current - 1, current, current + 1)
          pages.push(total)
        }
      }
      return pages
    },
    showEllipsis() {
      const total = this.totalPages
      return total > 7
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery, oldQuery) {
        let categoryChanged = false
        if (newQuery.keyword !== undefined) {
          this.searchKeyword = newQuery.keyword
        } else if (!newQuery.category && (!oldQuery || oldQuery.keyword)) {
          this.searchKeyword = ''
        }
        if (newQuery.category) {
          const newCatId = parseInt(newQuery.category) || 2
          if (this.activeCategoryId !== newCatId) {
            this.activeCategoryId = newCatId
            this.expandAncestors(newCatId)
            this.currentPage = 1
            categoryChanged = true
          }
        } else if (!newQuery.keyword) {
          if (this.activeCategoryId !== 2) {
            this.activeCategoryId = 2
            this.currentPage = 1
            categoryChanged = true
          }
        }
        if (!categoryChanged) {
          if (newQuery.page) {
            this.currentPage = parseInt(newQuery.page) || 1
          } else if (!oldQuery || (!oldQuery.page && newQuery.page === undefined)) {
            this.currentPage = 1
          }
        }
        this.fetchArticles()
      },
      immediate: false
    }
  },
  mounted() {
    const queryCategory = this.$route.query.category
    const queryKeyword = this.$route.query.keyword
    const queryPage = this.$route.query.page
    if (queryKeyword) {
      this.searchKeyword = queryKeyword
    }
    if (queryCategory) {
      this.activeCategoryId = parseInt(queryCategory) || 2
    }
    if (queryPage) {
      this.currentPage = parseInt(queryPage) || 1
    }
    console.log('初始分类ID:', this.activeCategoryId, '搜索关键词:', this.searchKeyword, '初始页码:', this.currentPage)
    this.fetchCategories().then(() => {
      this.fetchArticles()
    })
    this.fetchHotArticles()
  },
  methods: {
    async fetchArticles() {
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize
        }
        if (!this.searchKeyword) {
          params.category_id = this.activeCategoryId
        }
        if (this.searchKeyword) {
          params.keyword = this.searchKeyword
        }
        const response = await axios.get('/api/posts', { params })
        console.log('=== 文章API完整响应 ===', response.data)
        this.articles = response.data.data
        console.log('=== articles直接赋值 ===', this.articles)
        const total = response.data.total || this.articles.length
        this.totalItems = total
        this.totalPages = Math.ceil(total / this.pageSize)
      } catch (error) {
        console.error('获取文章列表失败:', error)
        this.articles = []
      }
    },
    doSearch() {
      this.currentPage = 1
      if (this.searchKeyword.trim()) {
        this.$router.push({ path: '/articles', query: { keyword: this.searchKeyword.trim() } })
      } else {
        this.$router.push({ path: '/articles' })
      }
    },
    async fetchHotArticles() {
      try {
        const response = await axios.get('/api/posts', {
          params: {
            page: 1,
            size: 5
          }
        })
        this.hotArticles = response.data.data
      } catch (error) {
        console.error('获取热门文章失败:', error)
        this.hotArticles = []
      }
    },
    async fetchCategories() {
      try {
        const response = await axios.get('/api/categories')
        console.log('分类API原始响应:', response)
        
        if (response.data && response.data.success) {
          this.categories = Array.isArray(response.data.data) ? response.data.data : []
          this.flatCategories = Array.isArray(response.data.flat) ? response.data.flat : []
        } else {
          this.categories = []
          this.flatCategories = []
        }
        
        if (this.flatCategories.length === 0) {
          this.flatCategories = this.extractFlatCategories(this.categories)
        }
        
        this.expandAncestors(this.activeCategoryId)
        console.log('处理后的分类数据:', this.categories, '扁平分类:', this.flatCategories)
      } catch (error) {
        console.error('获取分类失败:', error)
        this.categories = [
          { id: 2, name: '新闻中心', count: 10, children: [] },
          { id: 3, name: '公告栏', count: 5, children: [] }
        ]
        this.flatCategories = [
          { id: 2, name: '新闻中心', parent: 0 },
          { id: 3, name: '公告栏', parent: 0 }
        ]
      }
    },
    extractFlatCategories(cats, parentId = 0) {
      const result = []
      const arr = Array.isArray(cats) ? cats : []
      arr.forEach(cat => {
        if (cat) {
          result.push({
            id: (cat.id !== undefined && cat.id !== null) ? cat.id : (cat.ID !== undefined && cat.ID !== null ? cat.ID : 0),
            name: (cat.name || '').toString(),
            parent: parentId
          })
          if (cat.children && Array.isArray(cat.children)) {
            result.push(...this.extractFlatCategories(cat.children, cat.id || 0))
          }
        }
      })
      return result
    },
    getBreadcrumbPath(catId) {
      const path = []
      let currentId = catId
      
      const catMap = {}
      this.flatCategories.forEach(c => {
        catMap[c.id] = c
      })
      
      while (currentId && currentId !== 0) {
        const cat = catMap[currentId]
        if (cat) {
          path.unshift({ id: cat.id, name: cat.name })
          currentId = cat.parent
        } else {
          break
        }
      }
      
      if (path.length === 0) {
        path.push(this.defaultRootCat)
      }
      
      return path
    },
    expandAncestors(catId) {
      const catMap = {}
      this.flatCategories.forEach(c => {
        catMap[c.id] = c
      })
      
      let currentId = catId
      while (currentId && currentId !== 0) {
        if (!this.expandedCats.includes(currentId)) {
          this.expandedCats.push(currentId)
        }
        const cat = catMap[currentId]
        if (!cat) break
        currentId = cat.parent
      }
    },
    async goToDetail(article) {
      if (typeof article === 'object' && article.external_link) {
        await this.handleExternalLink(article)
      } else {
        const id = typeof article === 'object' ? article.id : article
        this.$router.push(`/article/${id}`)
      }
    },
    updateUrlWithPage() {
      const newQuery = { ...this.$route.query }
      if (this.currentPage > 1) {
        newQuery.page = this.currentPage
      } else {
        delete newQuery.page
      }
      this.$router.replace({ path: '/articles', query: newQuery })
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.updateUrlWithPage()
        this.fetchArticles()
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.updateUrlWithPage()
        this.fetchArticles()
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page
        this.updateUrlWithPage()
        this.fetchArticles()
      }
    },
    onPageSizeChange() {
      this.currentPage = 1
      this.fetchArticles()
    },
    filterByCategory(categoryId) {
      this.searchKeyword = ''
      this.activeCategoryId = categoryId
      this.expandAncestors(categoryId)
      this.currentPage = 1
      this.$router.push({ path: '/articles', query: { category: categoryId } })
    },
    toggleExpand(catId) {
      const index = this.expandedCats.indexOf(catId)
      if (index > -1) {
        this.expandedCats.splice(index, 1)
      } else {
        this.expandedCats.push(catId)
      }
    },
    async handleExternalLink(article) {
      try {
        const response = await axios.get(`/api/hit/${article.id}`)
        console.log('外链浏览记录写入成功:', response.data)
        const link = response.data.external_link || article.external_link
        window.open(link, '_blank')
      } catch (err) {
        console.error('记录外链浏览失败，直接跳转:', err)
        window.open(article.external_link, '_blank')
      }
    }
  }
}
</script>

<style lang="less" scoped>
.article-list-page {
  min-height: 100vh;
  background: #f5f5f5;
}

.page-header {
  position: sticky;
  top: 198px;
  z-index: 150;
  background: white;
  border-bottom: 1px solid #eee;
  padding: 15px 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }

  .header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
  }

  .header-left {
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    font-size: 14px;
    color: #666;
    margin-left: 30px;

    a {
      color: #4ecdc4;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    span {
      margin: 0 8px;
      color: #999;
    }
  }

  .current-category-title {
    font-size: 24px;
    font-weight: bold;
    color: #4ecdc4;
    margin: 0;
  }

  .search-box {
    display: flex;
    gap: 10px;
    align-items: center;

    input {
      padding: 8px 15px;
      border: 1px solid #ddd;
      border-radius: 5px;
      font-size: 14px;
      width: 220px;
      outline: none;

      &:focus {
        border-color: #4ecdc4;
      }
    }

    button {
      padding: 8px 20px;
      background: #4ecdc4;
      color: white;
      border: none;
      border-radius: 5px;
      font-size: 14px;
      cursor: pointer;
      transition: opacity 0.3s;

      &:hover {
        opacity: 0.9;
      }
    }
  }
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 30px 20px;
  display: flex;
  gap: 30px;
}

.sidebar {
  width: 300px;
  flex-shrink: 0;
  position: sticky;
  top: 175px;
  align-self: flex-start;
}

.sidebar-section {
  background: white;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 16px;
    font-weight: bold;
    color: #333;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #4ecdc4;
  }
}

.category-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li.tree-item {
    border-bottom: 1px dashed #eee;

    &:last-child {
      border-bottom: none;
    }

    .tree-row {
      display: flex;
      align-items: center;
      padding: 6px 0;

      .expand-icon {
        cursor: pointer;
        font-size: 8px;
        width: 12px;
        height: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 6px;
        color: #4ecdc4;

        &.empty {
          cursor: default;
          color: transparent;
        }
      }

      a {
        flex: 1;
        font-size: 14px;
        color: #666;
        text-decoration: none;

        &:hover, &.active {
          color: #4ecdc4;
          font-weight: bold;
        }
      }
    }

    .sub-category {
      list-style: none;
      padding: 0 0 0 20px;
      margin: 0;

      li.tree-item {
        border-bottom: none;
      }
    }
  }
}

.main-content {
  flex: 1;
  
  &.full-width {
    width: 100%;
    flex: none;
  }
}

.article-list {
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 10px;
  padding: 15px 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.article-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px dashed #eee;
  cursor: pointer;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fa;

    .article-title {
      color: #4ecdc4;
    }
  }

  .article-bullet {
    color: #00AEAA;
    font-size: 20px;
    margin-right: 12px;
    line-height: 1;
    flex-shrink: 0;
  }

  .article-title {
    flex: 1;
    font-size: 16px;
    color: #333;
    transition: color 0.2s;
  }

  .external-link-icon {
    color: #999;
    margin: 0 6px;
    flex-shrink: 0;
  }

  .article-date {
    font-size: 14px;
    color: #999;
    margin-left: 20px;
    flex-shrink: 0;
  }
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;
  margin-top: 30px;
  padding: 20px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);

  .total-text {
    font-size: 14px;
    color: #333;
    margin-right: 10px;
    line-height: 1.4;
  }

  .page-btn {
    width: 38px;
    height: 32px;
    background: white;
    color: #333;
    border: 1px solid #eee;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      border-color: #4ecdc4;
      color: #4ecdc4;
    }

    &.active {
      border-color: #4ecdc4;
      color: #4ecdc4;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .ellipsis {
    font-size: 14px;
    color: #999;
    width: 24px;
    text-align: center;
  }

  .page-size-select {
    width: 100px;
    height: 32px;
    background: white;
    color: #333;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    cursor: pointer;
    padding: 0 30px 0 10px;
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    transition: border-color 0.3s;
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%234ecdc4' d='M6 8L0 2L1.4 0.6L6 5.2L10.6 0.6L12 2L6 8Z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    
    &:focus {
      border-color: #4ecdc4;
    }
  }

  .page-size-select option {
    background: white;
    color: #333;
    
    &:checked {
      background: #4ecdc4;
      color: white;
    }
  }
}
</style>
